import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Autocomplete,
  Divider,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { saveOrEditAppointmentMapping, getGhlCalendars, getGhlUsers } from "../../Service/AxiosService";

// DTO converted from C# to TypeScript
export interface AppointmentDto {
  DateFieldId: string;
  TypeFieldId: string;
  LocationFieldId: string;
  ProviderFieldId: string;
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const DROPDOWN_WIDTH = 300;

const AddorUpdateAppointment = (props: {
  open: boolean;
  onClose: () => void;
  cerboTags: { tagId: string; tagName: string }[];
  crmTags: { tagId: string; tagName: string }[];
  customFields: { fieldId: string; fieldName: string }[];
  initialValues?: any;
  providers: { providerId: string; providerName: string }[];
  appointmentData: { appointmentTypeId: string; appointmentTypeName: string }[];
}) => {
  const [appointType, setAppointType] = useState("");
  const [enableTagCondition, setEnableTagCondition] = useState(false);
  const [cerboTagIds, setCerboTagIds] = useState<string[]>([]);
  const [crmTagIds, setCrmTagIds] = useState<string[]>([]);
  const [customFieldIds, setCustomFieldIds] = useState<string[]>([]);
  const [enableStrtover, setEnableStrtover] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [providers, setProviders] = useState('');
  const [AppointmentType, setAppointmentType] = useState<string[]>([]);
  const [AppointmentLocation, setAppointmentLocation] = useState<string[]>([]);
  const [AppointmentProvider, setAppointmentProvider] = useState<string[]>([]);
  const [AppointmentDate, setAppointmentDate] = useState<string[]>([]);
  const [enableProviderSelection, setEnableProviderSelection] = useState(false);
  // Calendar feature: choose a real GHL calendar (+ optional assigned user) for this appointment type.
  const [ghlCalendars, setGhlCalendars] = useState<{ id: string; name: string; teamMembers?: { userId: string }[] }[]>([]);
  const [ghlCalendarId, setGhlCalendarId] = useState("");
  const [ghlAssignedUserId, setGhlAssignedUserId] = useState("");
  // HL users for the assigned-user dropdown (empty unless the location is connected with users.readonly).
  const [ghlUsers, setGhlUsers] = useState<{ id: string; name: string; firstName?: string; lastName?: string }[]>([]);

  // Load the connected location's real HL calendars + users when the dialog opens (empty if not connected).
  useEffect(() => {
    if (!props.open) return;
    (async () => {
      const res = await getGhlCalendars();
      setGhlCalendars(res.success && Array.isArray(res.data) ? res.data : []);
      const usersRes = await getGhlUsers();
      setGhlUsers(usersRes.success && Array.isArray(usersRes.data) ? usersRes.data : []);
    })();
  }, [props.open]);

  useEffect(() => {
    if (props.initialValues) {
      setAppointType(props.initialValues.AppointmentTitle || "");
      setGhlCalendarId(props.initialValues.GhlCalendarId || "");
      setGhlAssignedUserId(props.initialValues.GhlAssignedUserId || "");
      setEnableTagCondition(props.initialValues.HasTagCondition === "Yes");
      setCerboTagIds(
        props.cerboTags
          .filter((tag) =>
            props.initialValues?.RequiredCerboTags.includes(tag.tagName),
          )
          .map((tag) => tag.tagId),
      );
      setCrmTagIds(
        props.crmTags
          .filter((tag) =>
            props.initialValues?.RequiredAcTags.includes(tag.tagName),
          )
          .map((tag) => tag.tagId),
      );
      // Read the saved custom-field mapping back from the stored slot IDs (customFieldJson), NOT by
      // guessing the slot from the field NAME — clinic field names like "DPC New Patient" don't
      // contain "Date"/"Type"/etc., so name-matching left every dropdown blank on edit.
      const slots: any[] = props.initialValues?.CustomFieldSlots || [];
      const slotIds = (key: string): string[] =>
        slots
          .map((s: any) => s?.[key])
          .filter((id: any) => id && String(id).trim() !== "");

      setAppointmentType(slotIds("TypeFieldId"));
      setAppointmentLocation(slotIds("LocationFieldId"));
      setAppointmentProvider(slotIds("ProviderFieldId"));
      setAppointmentDate(slotIds("DateFieldId"));

      // `providers` state holds the provider ID (matches the dropdown value + save). Resolve the saved
      // provider NAME back to its ID here.
      const matchedProvider = props.providers.find(a => a.providerName === props.initialValues?.Providers);
      setProviders(matchedProvider?.providerId || '');
      // Restore the "Enable Provider Selection" toggle on edit — otherwise the provider section stays
      // collapsed, the saved provider is invisible, and the next save wipes it.
      setEnableProviderSelection(!!matchedProvider || !!props.initialValues?.enableProviderSelection);
      setEnableStrtover(props.initialValues.IsStartOver === "Yes");
    } else {
      setAppointType("");
      setEnableTagCondition(false);
      setCerboTagIds([]);
      setCrmTagIds([]);
      setCustomFieldIds([]);
      setEnableStrtover(false);
      setProviders("");
      // Full reset so a new rule never inherits the previous (edited) rule's custom-field mappings
      // or provider toggle when the dialog was dismissed via backdrop/Escape (which skips resetForm).
      setAppointmentType([]);
      setAppointmentLocation([]);
      setAppointmentDate([]);
      setAppointmentProvider([]);
      setEnableProviderSelection(false);
      setGhlCalendarId("");
      setGhlAssignedUserId("");
    }
    setError(null);
  }, [props.initialValues, props.open]);

  const isStep1Valid = appointType.trim() !== "";
  const isStep2Valid =
    !enableTagCondition || cerboTagIds.length > 0 || crmTagIds.length > 0;
  // Appointment custom fields are optional and independent — a practice can map just the Date,
  // or Date+Type, etc. (1, 2, 3, or all). The rule just needs to do at least one thing:
  // map at least one appointment custom field, OR a real HL calendar.
  const anyCustomField =
    AppointmentType.length > 0 ||
    AppointmentLocation.length > 0 ||
    AppointmentDate.length > 0 ||
    AppointmentProvider.length > 0;
  const hasAction = anyCustomField || !!ghlCalendarId;
  const isFormValid = isStep1Valid && isStep2Valid && hasAction;

  const resetForm = () => {
    setAppointType("");
    setEnableTagCondition(false);
    setCerboTagIds([]);
    setCrmTagIds([]);
    setCustomFieldIds([]);
    setEnableStrtover(false);
    setError(null);
    setProviders("");
    setAppointmentType([]);
    setAppointmentLocation([]);
    setAppointmentDate([]);
    setAppointmentProvider([]);
    setEnableProviderSelection(false);
    setGhlCalendarId("");
    setGhlAssignedUserId("");
  };

  const handleCancel = () => {
    resetForm();
    props.onClose();
  };

  const renderChips = (
    selected: string[],
    options: any[],
    labelKey: string,
    valueKey: string,
  ) => (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}
    >
      {selected.slice(0, 3).map((id) => {
        const label = options.find((t) => t[valueKey] === id)?.[labelKey];
        return <Chip key={id} label={label} size="small" />;
      })}
      {selected.length > 3 && (
        <Chip size="small" label={`+${selected.length - 3}`} />
      )}
    </Box>
  );

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    let customFIeldIdsString = new Array<AppointmentDto>();
    // Iterate over the most-filled field so a partial selection (e.g. Date only, with Type empty)
    // still saves — was keyed off AppointmentType.length, which dropped everything when Type was blank.
    const slotCount = Math.max(
      AppointmentType.length,
      AppointmentDate.length,
      AppointmentLocation.length,
      AppointmentProvider.length,
    );
    for (let i = 0; i < slotCount; i++) {
      let Appointmentdto: AppointmentDto = {
        DateFieldId: AppointmentDate[i] || "",
        TypeFieldId: AppointmentType[i] || "",
        LocationFieldId: AppointmentLocation[i] || "",
        ProviderFieldId: AppointmentProvider[i] || "",
      };
      customFIeldIdsString.push(Appointmentdto);
    }
    try {
      await saveOrEditAppointmentMapping({
        id: props.initialValues?.id || undefined,
        appointmentType:
          props.appointmentData.find(
            (type) => type.appointmentTypeName === appointType,
          )?.appointmentTypeId || "",
        hasTagCondition: enableTagCondition,
        requiredCerboTags: cerboTagIds.join(",").toString(),
        requiredCRMTags: crmTagIds.join(",").toString(),
        isStartOver: enableStrtover,
        customFieldJson: customFIeldIdsString.length > 0 ? JSON.stringify(customFIeldIdsString) : undefined,
        EnableProvider: enableProviderSelection && !!providers,
        // `providers` already holds the provider ID; validate it still exists in the list.
        providerId: enableProviderSelection ? (props.providers.find((provider: any) => provider.providerId === providers)?.providerId || "") : "",
        ghlCalendarId: ghlCalendarId || undefined,
        ghlAssignedUserId: ghlAssignedUserId || undefined,
      });
      setSaving(false);
      resetForm();
      props.onClose();
    } catch (err) {
      setSaving(false);
      setError("Failed to save. Please try again.");
    }
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: "#FAFAFC",
          boxShadow: "0 2px 16px rgba(24,26,42,0.10)",
          minWidth: 320,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>
        Configure Appointment Rule
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          p: { xs: 2, sm: 3 },
          maxHeight: "80vh",
          overflowY: "auto",
          minHeight: 0,
          background: "#FAFAFC",
        }}
      >
        <Box sx={{ width: "100%" }}>
          <Box sx={{ display: "flex", gap: 1, position: "relative" }}>
            <Box className="step-number">1</Box>
            <Box
              sx={{
                position: "absolute",
                left: 19,
                top: 38,
                bottom: 0,
                width: 2,
                bgcolor: "#e2e8f0",
              }}
            />
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  bgcolor: "#f3f9ff",
                  px: 2,
                  py: 1,
                  borderRadius: 4,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, fontSize: 14 }}
                >
                  Select Appointment Type
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#555" }}>
                  Choose the appointment type. This rule will be triggered when
                  this appointment type is added in Cerbo for a Patient on Sync.
                </Typography>
              </Box>

              <Grid
                container
                spacing={2}
                direction="column"
                sx={{ mt: 3, mb: 4, ml: 3 }}
              >
                <Grid>
                  <Autocomplete
                    options={props.appointmentData}
                    getOptionLabel={(option) => option.appointmentTypeName}
                    slotProps={{
                      paper: {
                        sx: {
                          "& .MuiAutocomplete-option": {
                            fontSize: 13,
                            minHeight: 32,
                          },
                        },
                      },
                    }}
                    value={
                      props.appointmentData.find(
                        (type) => type.appointmentTypeName === appointType,
                      ) || undefined
                    }
                    onChange={(_, newValue) =>
                      setAppointType(
                        newValue ? newValue.appointmentTypeName : "",
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Appointment Type"
                        size="small"
                        fullWidth
                        variant="outlined"
                        sx={{
                          bgcolor: "#fff",
                          borderRadius: 2,
                          "& .MuiOutlinedInput-root": {
                            fontSize: 14,
                            color: "#23244A",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "#dedede",
                            },
                            "&:hover fieldset": {
                              borderColor: "#3D52A0",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#3D52A0",
                            },
                          },

                          "& .MuiInputLabel-root": {
                            fontSize: 13,
                            color: "#23244A",
                          },
                        }}
                      />
                    )}
                    sx={{
                      width: "50%",
                      bgcolor: "#fff",
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(24,26,42,0.08)",
                    }}
                    disableClearable
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1, position: "relative" }}>
            <Box className="step-number">2</Box>
            <Box
              sx={{
                position: "absolute",
                left: 19,
                top: 38,
                bottom: 0,
                width: 2,
                bgcolor: "#e2e8f0",
              }}
            />
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  bgcolor: "#f3f9ff",
                  px: 2,
                  py: 1,
                  borderRadius: 4,
                }}
              >
                <Grid direction='row' container alignItems="center" gap={0.5}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, fontSize: 14 }}
                  >
                    Tag Condition
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, fontSize: 13, color: "#555" }}
                  >
                    (Optional)
                  </Typography>
                </Grid>
                <Typography sx={{ fontSize: 11, color: "#555" }}>
                  Enable and select tags to further restrict when this rule applies.
                </Typography>
              </Box>

              <Grid
                container
                spacing={2}
                direction="column"
                sx={{ mt: 2, mb: 4, ml: 3 }}
              >
                <Grid>
                  <FormControlLabel
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: 13,
                        color: "#475569",
                      },
                    }}
                    control={
                      <Checkbox
                        checked={enableTagCondition}
                        style={{ marginRight: 2 }}
                        onChange={(e) =>
                          setEnableTagCondition(e.target.checked)
                        }
                        sx={{
                          "& .MuiSvgIcon-root": {
                            fontSize: 18,
                          },
                          color: "#94a3b8",
                        }}
                      />
                    }
                    label="Enable Tag Condition"
                  />
                </Grid>
                {enableTagCondition && (
                  <>
                    <Grid>
                      <Autocomplete
                        multiple
                        options={props.cerboTags}
                        getOptionLabel={(option) => option.tagName}
                        disableCloseOnSelect
                        slotProps={{
                          paper: {
                            sx: {
                              "& .MuiAutocomplete-option": {
                                fontSize: 13,
                                minHeight: 32,
                              },
                            },
                          },
                        }}
                        value={props.cerboTags.filter((tag) =>
                          cerboTagIds.includes(tag.tagId),
                        )}
                        onChange={(_, newValue) =>
                          setCerboTagIds(newValue.map((tag) => tag.tagId))
                        }
                        renderOption={(props, option, { selected }) => (
                          <li {...props}>
                            <Checkbox
                              icon={icon}
                              checkedIcon={checkedIcon}
                              style={{ marginRight: 8 }}
                              checked={selected}
                            />
                            {option.tagName}
                          </li>
                        )}
                        renderTags={(value, getTagProps) =>
                          renderChips(
                            value.map((tag) => tag.tagId),
                            props.cerboTags,
                            "tagName",
                            "tagId",
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Cerbo Tags"
                            size="small"
                            variant="outlined"
                            fullWidth
                            sx={{
                              bgcolor: "#fff",
                              borderRadius: 2,
                              "& .MuiOutlinedInput-root": {
                                fontSize: 14,
                                color: "#23244A",
                                borderRadius: 2,
                                "& fieldset": {
                                  borderColor: "#dedede",
                                },
                                "&:hover fieldset": {
                                  borderColor: "#3D52A0",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#3D52A0",
                                },
                              },

                              "& .MuiInputLabel-root": {
                                fontSize: 13,
                                color: "#23244A",
                              },
                            }}
                          />
                        )}
                        sx={{
                          width: "50%",
                          bgcolor: "#fff",
                          borderRadius: 2,
                          boxShadow: "0 2px 8px rgba(24,26,42,0.08)",
                        }}
                      />
                    </Grid>
                    <Grid>
                      <Autocomplete
                        multiple
                        options={props.crmTags}
                        getOptionLabel={(option) => option.tagName}
                        disableCloseOnSelect
                        slotProps={{
                          paper: {
                            sx: {
                              "& .MuiAutocomplete-option": {
                                fontSize: 13,
                                minHeight: 32,
                              },
                            },
                          },
                        }}
                        value={props.crmTags.filter((tag) =>
                          crmTagIds.includes(tag.tagId),
                        )}
                        onChange={(_, newValue) =>
                          setCrmTagIds(newValue.map((tag) => tag.tagId))
                        }
                        renderOption={(props, option, { selected }) => (
                          <li {...props}>
                            <Checkbox
                              icon={icon}
                              checkedIcon={checkedIcon}
                              style={{ marginRight: 8 }}
                              checked={selected}
                            />
                            {option.tagName}
                          </li>
                        )}
                        renderTags={(value, getTagProps) =>
                          renderChips(
                            value.map((tag) => tag.tagId),
                            props.crmTags,
                            "tagName",
                            "tagId",
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="HL Tags"
                            size="small"
                            fullWidth
                            variant="outlined"
                            sx={{
                              bgcolor: "#fff",
                              borderRadius: 2,
                              "& .MuiOutlinedInput-root": {
                                fontSize: 14,
                                color: "#23244A",
                                borderRadius: 2,
                                "& fieldset": {
                                  borderColor: "#dedede",
                                },
                                "&:hover fieldset": {
                                  borderColor: "#3D52A0",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#3D52A0",
                                },
                              },

                              "& .MuiInputLabel-root": {
                                fontSize: 13,
                                color: "#23244A",
                              },
                            }}
                          />
                        )}
                        sx={{
                          width: "50%",
                          bgcolor: "#fff",
                          borderRadius: 2,
                          boxShadow: "0 2px 8px rgba(24,26,42,0.08)",
                        }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1, position: "relative" }}>
            <Box className="step-number">3</Box>
            <Box
              sx={{
                position: "absolute",
                left: 19,
                top: 38,
                bottom: 0,
                width: 2,
                bgcolor: "#e2e8f0",
              }}
            />
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  bgcolor: "#f3f9ff",
                  px: 2,
                  py: 1,
                  borderRadius: 4,
                }}
              >
                <Grid direction='row' container alignItems="center" gap={0.5}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, fontSize: 14 }}
                  >
                    Enable Provider
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, fontSize: 13, color: "#555" }}
                  >
                    (Optional)
                  </Typography>
                </Grid>
                <Typography sx={{ fontSize: 11, color: "#555" }}>
                  Enable and select Providers to further restrict when this rule applies.
                </Typography>
              </Box>

              <Grid
                container
                spacing={2}
                direction="column"
                sx={{ mt: 2, mb: 4, ml: 3 }}
              >
                <Grid>
                  <FormControlLabel
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: 13,
                        color: "#475569",
                      },
                    }}
                    control={
                      <Checkbox
                        checked={enableProviderSelection}
                        style={{ marginRight: 2 }}
                        onChange={(e) =>
                          setEnableProviderSelection(e.target.checked)
                        }
                        sx={{
                          "& .MuiSvgIcon-root": {
                            fontSize: 18,
                          },
                          color: "#94a3b8",
                        }}
                      />
                    }
                    label="Enable Provider Selection"
                  />
                </Grid>
                {enableProviderSelection && (
                  <>
                    <Grid>
                      <Autocomplete
                        options={props.providers}
                        getOptionLabel={(option) => option.providerName}
                        slotProps={{
                          paper: {
                            sx: {
                              "& .MuiAutocomplete-option": {
                                fontSize: 13,
                                minHeight: 32,
                              },
                            },
                          },
                        }}
                        value={props.providers.find((tag) => tag.providerId === providers) || null}
                        onChange={(_, newValue) =>
                          setProviders(newValue ? newValue.providerId : "")
                        }
                        renderTags={(value, getTagProps) =>
                          renderChips(
                            value.map((tag) => tag.providerId),
                            props.providers,
                            "providerName",
                            "providerId",
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Providers"
                            size="small"
                            variant="outlined"
                            fullWidth
                            sx={{
                              bgcolor: "#fff",
                              borderRadius: 2,
                              "& .MuiOutlinedInput-root": {
                                fontSize: 14,
                                color: "#23244A",
                                borderRadius: 2,
                                "& fieldset": {
                                  borderColor: "#dedede",
                                },
                                "&:hover fieldset": {
                                  borderColor: "#3D52A0",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#3D52A0",
                                },
                              },

                              "& .MuiInputLabel-root": {
                                fontSize: 13,
                                color: "#23244A",
                              },
                            }}
                          />
                        )}
                        sx={{
                          width: "50%",
                          bgcolor: "#fff",
                          borderRadius: 2,
                          boxShadow: "0 2px 8px rgba(24,26,42,0.08)",
                        }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1, position: "relative" }}>
            <Box className="step-number">4</Box>
            <Box
              sx={{
                position: "absolute",
                left: 19,
                top: 38,
                bottom: 0,
                width: 2,
                bgcolor: "#e2e8f0",
              }}
            />
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  bgcolor: "#f3f9ff",
                  px: 2,
                  py: 1,
                  borderRadius: 4,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, fontSize: 14 }}
                >
                  Custom Field (Optional)
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#555" }}>
                  Optionally map appointment details to HL custom fields — pick any
                  you need (e.g. just Date, or all four). Leave blank if you only
                  need the HL calendar.
                </Typography>
              </Box>

              <Grid
                container
                spacing={1}
                direction="row"

                sx={{ mt: 3, mb: 4 }}
              >
                <Grid size={6}>
                  <Autocomplete
                    multiple
                    fullWidth
                    options={props.customFields}
                    getOptionLabel={(option) => option.fieldName}
                    disableCloseOnSelect
                    slotProps={{
                      paper: {
                        sx: {
                          "& .MuiAutocomplete-option": {
                            fontSize: 13,
                            minHeight: 32,
                          },
                        },
                      },
                    }}
                    value={AppointmentType.map(id => props.customFields.find(f => f.fieldId === id)).filter(Boolean) as typeof props.customFields}
                    onChange={(_, newValue) => {
                      const newIds = newValue.map(v => v.fieldId);
                      const addedIds = newIds.filter(id => !AppointmentType.includes(id));
                      const remainingIds = AppointmentType.filter(id => newIds.includes(id));
                      setAppointmentType([...remainingIds, ...addedIds]);
                    }}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.fieldName}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Appointment Type Field"
                        size="small"
                        fullWidth
                        sx={{
                          bgcolor: "#fff",
                          borderRadius: 2,
                          "& .MuiOutlinedInput-root": {
                            fontSize: 14,
                            color: "#23244A",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "#dedede",
                            },
                            "&:hover fieldset": {
                              borderColor: "#3D52A0",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#3D52A0",
                            },
                          },

                          "& .MuiInputLabel-root": {
                            fontSize: 13,
                            color: "#23244A",
                          },
                        }}
                      />
                    )}
                    sx={{
                      width: "90%",
                      bgcolor: "#fff",
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(24,26,42,0.08)",
                      ml: 3
                    }}
                    disableClearable
                  />
                </Grid>
                <Grid size={6}>
                  <Box sx={{ pl: 2 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#23244A", mb: 1 }}>
                      Selected Fields Order:
                    </Typography>
                    {AppointmentType.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography sx={{ fontSize: 11, color: "#555", fontWeight: 600 }}>Custom Fields:</Typography>
                        {AppointmentType.map((fieldId, index) => {
                          const fieldName = props.customFields.find(f => f.fieldId === fieldId)?.fieldName || fieldId;
                          return (
                            <Typography key={fieldId} sx={{ fontSize: 11, color: "#475569" }}>
                              {index + 1}: {fieldName}
                            </Typography>
                          );
                        })}
                      </Box>
                    )}
                    {AppointmentType.length === 0 && (
                      <Typography sx={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>
                        No fields selected
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>

              <Grid
                container
                spacing={1}
                direction="row"

                sx={{ mt: 3, mb: 4 }}
              >
                <Grid size={6}>
                  <Autocomplete
                    multiple
                    fullWidth
                    options={props.customFields}
                    getOptionLabel={(option) => option.fieldName}
                    disableCloseOnSelect
                    slotProps={{
                      paper: {
                        sx: {
                          "& .MuiAutocomplete-option": {
                            fontSize: 13,
                            minHeight: 32,
                          },
                        },
                      },
                    }}
                    value={AppointmentLocation.map(id => props.customFields.find(f => f.fieldId === id)).filter(Boolean) as typeof props.customFields}
                    onChange={(_, newValue) => {
                      const newIds = newValue.map(v => v.fieldId);
                      const addedIds = newIds.filter(id => !AppointmentLocation.includes(id));
                      const remainingIds = AppointmentLocation.filter(id => newIds.includes(id));
                      setAppointmentLocation([...remainingIds, ...addedIds]);
                    }}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.fieldName}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Appointment Location Field"
                        size="small"
                        fullWidth
                        sx={{
                          bgcolor: "#fff",
                          borderRadius: 2,
                          "& .MuiOutlinedInput-root": {
                            fontSize: 14,
                            color: "#23244A",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "#dedede",
                            },
                            "&:hover fieldset": {
                              borderColor: "#3D52A0",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#3D52A0",
                            },
                          },

                          "& .MuiInputLabel-root": {
                            fontSize: 13,
                            color: "#23244A",
                          },
                        }}
                      />
                    )}
                    sx={{
                      width: "90%",
                      bgcolor: "#fff",
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(24,26,42,0.08)",
                      ml: 3
                    }}
                    disableClearable
                  />
                </Grid>
                <Grid size={6}>
                  <Box sx={{ pl: 2 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#23244A", mb: 1 }}>
                      Selected Fields Order:
                    </Typography>
                    {AppointmentLocation.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography sx={{ fontSize: 11, color: "#555", fontWeight: 600 }}>Custom Fields:</Typography>
                        {AppointmentLocation.map((fieldId, index) => {
                          const fieldName = props.customFields.find(f => f.fieldId === fieldId)?.fieldName || fieldId;
                          return (
                            <Typography key={fieldId} sx={{ fontSize: 11, color: "#475569" }}>
                              {index + 1}: {fieldName}
                            </Typography>
                          );
                        })}
                      </Box>
                    )}
                    {AppointmentLocation.length === 0 && (
                      <Typography sx={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>
                        No fields selected
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>

              <Grid
                container
                spacing={1}
                direction="row"

                sx={{ mt: 3, mb: 4 }}
              >
                <Grid size={6}>
                  <Autocomplete
                    multiple
                    fullWidth
                    options={props.customFields}
                    getOptionLabel={(option) => option.fieldName}
                    disableCloseOnSelect
                    slotProps={{
                      paper: {
                        sx: {
                          "& .MuiAutocomplete-option": {
                            fontSize: 13,
                            minHeight: 32,
                          },
                        },
                      },
                    }}
                    value={AppointmentDate.map(id => props.customFields.find(f => f.fieldId === id)).filter(Boolean) as typeof props.customFields}
                    onChange={(_, newValue) => {
                      const newIds = newValue.map(v => v.fieldId);
                      const addedIds = newIds.filter(id => !AppointmentDate.includes(id));
                      const remainingIds = AppointmentDate.filter(id => newIds.includes(id));
                      setAppointmentDate([...remainingIds, ...addedIds]);
                    }}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.fieldName}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Appointment Date Field"
                        size="small"
                        fullWidth
                        sx={{
                          bgcolor: "#fff",
                          borderRadius: 2,
                          "& .MuiOutlinedInput-root": {
                            fontSize: 14,
                            color: "#23244A",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "#dedede",
                            },
                            "&:hover fieldset": {
                              borderColor: "#3D52A0",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#3D52A0",
                            },
                          },

                          "& .MuiInputLabel-root": {
                            fontSize: 13,
                            color: "#23244A",
                          },
                        }}
                      />
                    )}
                    sx={{
                      width: "90%",
                      bgcolor: "#fff",
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(24,26,42,0.08)",
                      ml: 3
                    }}
                    disableClearable
                  />
                </Grid>
                <Grid size={6}>
                  <Box sx={{ pl: 2 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#23244A", mb: 1 }}>
                      Selected Fields Order:
                    </Typography>
                    {AppointmentDate.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography sx={{ fontSize: 11, color: "#555", fontWeight: 600 }}>Custom Fields:</Typography>
                        {AppointmentDate.map((fieldId, index) => {
                          const fieldName = props.customFields.find(f => f.fieldId === fieldId)?.fieldName || fieldId;
                          return (
                            <Typography key={fieldId} sx={{ fontSize: 11, color: "#475569" }}>
                              {index + 1}: {fieldName}
                            </Typography>
                          );
                        })}
                      </Box>
                    )}
                    {AppointmentDate.length === 0 && (
                      <Typography sx={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>
                        No fields selected
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>

              <Grid
                container
                spacing={1}
                direction="row"

                sx={{ mt: 3, mb: 4 }}
              >
                <Grid size={6}>
                  <Autocomplete
                    multiple
                    fullWidth
                    options={props.customFields}
                    getOptionLabel={(option) => option.fieldName}
                    disableCloseOnSelect
                    slotProps={{
                      paper: {
                        sx: {
                          "& .MuiAutocomplete-option": {
                            fontSize: 13,
                            minHeight: 32,
                          },
                        },
                      },
                    }}
                    value={AppointmentProvider.map(id => props.customFields.find(f => f.fieldId === id)).filter(Boolean) as typeof props.customFields}
                    onChange={(_, newValue) => {
                      const newIds = newValue.map(v => v.fieldId);
                      const addedIds = newIds.filter(id => !AppointmentProvider.includes(id));
                      const remainingIds = AppointmentProvider.filter(id => newIds.includes(id));
                      setAppointmentProvider([...remainingIds, ...addedIds]);
                    }}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.fieldName}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Appointment Provider Field"
                        size="small"
                        fullWidth
                        sx={{
                          bgcolor: "#fff",
                          borderRadius: 2,
                          "& .MuiOutlinedInput-root": {
                            fontSize: 14,
                            color: "#23244A",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "#dedede",
                            },
                            "&:hover fieldset": {
                              borderColor: "#3D52A0",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#3D52A0",
                            },
                          },

                          "& .MuiInputLabel-root": {
                            fontSize: 13,
                            color: "#23244A",
                          },
                        }}
                      />
                    )}
                    sx={{
                      width: "90%",
                      bgcolor: "#fff",
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(24,26,42,0.08)",
                      ml: 3
                    }}
                    disableClearable
                  />
                </Grid>
                <Grid size={6}>
                  <Box sx={{ pl: 2 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#23244A", mb: 1 }}>
                      Selected Fields Order:
                    </Typography>
                    {AppointmentProvider.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography sx={{ fontSize: 11, color: "#555", fontWeight: 600 }}>Custom Fields:</Typography>
                        {AppointmentProvider.map((fieldId, index) => {
                          const fieldName = props.customFields.find(f => f.fieldId === fieldId)?.fieldName || fieldId;
                          return (
                            <Typography key={fieldId} sx={{ fontSize: 11, color: "#475569" }}>
                              {index + 1}: {fieldName}
                            </Typography>
                          );
                        })}
                      </Box>
                    )}
                    {AppointmentProvider.length === 0 && (
                      <Typography sx={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>
                        No fields selected
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1, position: "relative" }}>
            <Box className="step-number">5</Box>
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  bgcolor: "#f3f9ff",
                  px: 2,
                  py: 1,
                  borderRadius: 4,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, fontSize: 14 }}
                >
                  Startover Condition
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#555" }}>
                  Enable this if appointment date needs to be overwritten on
                  every sync.
                </Typography>
              </Box>

              <Grid
                container
                spacing={2}
                direction="column"
                sx={{ mt: 2, mb: 4, ml: 3 }}
              >
                <Grid>
                  <FormControlLabel
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: 13,
                        color: "#475569",
                      },
                    }}
                    control={
                      <Checkbox
                        checked={enableStrtover}
                        style={{ marginRight: 2 }}
                        onChange={(e) => setEnableStrtover(e.target.checked)}
                        sx={{
                          "& .MuiSvgIcon-root": {
                            fontSize: 18,
                          },
                          color: "#94a3b8",
                        }}
                      />
                    }
                    label="Enable Startover Condition"
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1, position: "relative" }}>
            <Box className="step-number">6</Box>
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  bgcolor: "#f3f9ff",
                  px: 2,
                  py: 1,
                  borderRadius: 4,
                }}
              >
                <Grid direction='row' container alignItems="center" gap={0.5}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, fontSize: 14 }}
                  >
                    HL Calendar (Real Appointment)
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, fontSize: 13, color: "#555" }}
                  >
                    (Optional)
                  </Typography>
                </Grid>
                <Typography sx={{ fontSize: 11, color: "#555" }}>
                  Map this appointment type to a real HL calendar. When set, a real
                  appointment is created on that calendar and kept in sync (reschedule /
                  cancel) with Cerbo, instead of only writing custom fields.
                </Typography>
              </Box>

              <Grid
                container
                spacing={2}
                direction="column"
                sx={{ mt: 2, mb: 4, ml: 3 }}
              >
                <Grid>
                  <Autocomplete
                    options={ghlCalendars}
                    getOptionLabel={(option) => option.name}
                    value={ghlCalendars.find((c) => c.id === ghlCalendarId) || null}
                    onChange={(_, newValue) => {
                      const newId = newValue ? newValue.id : "";
                      setGhlCalendarId(newId);
                      // Clear a stale assignee that isn't on the newly selected calendar's team —
                      // HL rejects an assignee that isn't a calendar team member (422).
                      const cal = ghlCalendars.find((c) => c.id === newId);
                      const teamIds = (cal?.teamMembers || []).map((t) => t.userId).filter(Boolean);
                      if (teamIds.length && ghlAssignedUserId && !teamIds.includes(ghlAssignedUserId)) {
                        setGhlAssignedUserId("");
                      }
                    }}
                    noOptionsText="No HL calendars found (connect the location first)"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="HL Calendar"
                        size="small"
                        fullWidth
                        variant="outlined"
                        sx={{
                          bgcolor: "#fff",
                          borderRadius: 2,
                          "& .MuiOutlinedInput-root": {
                            fontSize: 14,
                            color: "#23244A",
                            borderRadius: 2,
                            "& fieldset": { borderColor: "#dedede" },
                            "&:hover fieldset": { borderColor: "#3D52A0" },
                            "&.Mui-focused fieldset": { borderColor: "#3D52A0" },
                          },
                          "& .MuiInputLabel-root": { fontSize: 13, color: "#23244A" },
                        }}
                      />
                    )}
                    sx={{
                      width: "50%",
                      bgcolor: "#fff",
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(24,26,42,0.08)",
                    }}
                  />
                </Grid>
                {ghlCalendarId && (
                  <Grid>
                    <Autocomplete
                      options={(() => {
                        // Only users on the selected calendar's team are valid assignees — HL rejects
                        // anyone else. Fall back to all users if the calendar exposes no team roster.
                        const cal = ghlCalendars.find((c) => c.id === ghlCalendarId);
                        const teamIds = (cal?.teamMembers || []).map((t) => t.userId).filter(Boolean);
                        return teamIds.length ? ghlUsers.filter((u) => teamIds.includes(u.id)) : ghlUsers;
                      })()}
                      getOptionLabel={(option) =>
                        option.name?.trim() ||
                        `${option.firstName || ""} ${option.lastName || ""}`.trim() ||
                        option.id
                      }
                      value={ghlUsers.find((u) => u.id === ghlAssignedUserId) || null}
                      onChange={(_, newValue) =>
                        setGhlAssignedUserId(newValue ? newValue.id : "")
                      }
                      noOptionsText="No users on this calendar's team (add them in HighLevel)"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Assigned User (optional)"
                          size="small"
                          fullWidth
                          variant="outlined"
                          sx={{
                            bgcolor: "#fff",
                            borderRadius: 2,
                            "& .MuiOutlinedInput-root": {
                              fontSize: 14,
                              color: "#23244A",
                              borderRadius: 2,
                              "& fieldset": { borderColor: "#dedede" },
                              "&:hover fieldset": { borderColor: "#3D52A0" },
                              "&.Mui-focused fieldset": { borderColor: "#3D52A0" },
                            },
                            "& .MuiInputLabel-root": { fontSize: 13, color: "#23244A" },
                          }}
                        />
                      )}
                      sx={{
                        width: "50%",
                        bgcolor: "#fff",
                        borderRadius: 2,
                        boxShadow: "0 2px 8px rgba(24,26,42,0.08)",
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 2, background: "#FAFAFC" }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleCancel}
          sx={{
            borderRadius: 2,
            fontWeight: 500,
            minWidth: 110,
            border: "none",
            background: "#dedede",
            color: "#000",
            textTransform: "capitalize",
            "&:hover": { background: "#d6d6d6" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || !isFormValid}
          sx={{
            borderRadius: 2,
            fontWeight: 500,
            minWidth: 160,
            background: "#1eac53",
            textTransform: "capitalize",
            "&:hover": { background: "#1a9849" },
          }}
        >
          {saving ? "Saving..." : "Save & Continue"}
        </Button>
      </DialogActions>
      {error && (
        <Box sx={{ color: "red", textAlign: "center", pb: 2 }}>{error}</Box>
      )}
    </Dialog>
  );
};

export default AddorUpdateAppointment;
